import { useEffect, useMemo, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { api } from '../api/client';
import type { AuditLog, AuditLogFilters, RealmInfo } from '../api/types';
import { Card, Badge, Alert, DataTable } from '../components/ui';
import type { BadgeTone, DataTableColumn } from '../components/ui';
import { hasAuditRole } from '../lib/jwtRoles';
import { saveBlob } from '../lib/saveBlob';
import { AuditDetails } from './auditLogs/AuditDetails';

/** Facet values plus the current selection, if the facets no longer include it. */
function withSelected(values: string[], selected: string): string[] {
  return selected === 'all' || values.includes(selected) ? values : [...values, selected];
}

const SELECT = 'px-3 py-2 border border-border-token rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500';

export default function AuditLogs() {
  const auth = useAuth();
  const allowed = hasAuditRole(auth.user?.access_token);

  const [realm, setRealm] = useState(() => api.currentRealm());
  const [realms, setRealms] = useState<RealmInfo[]>([]);
  const [entityFilter, setEntityFilter] = useState('all');
  const [actionFilter, setActionFilter] = useState('all');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [pageSize, setPageSize] = useState(25);
  const [reloadTick, setReloadTick] = useState(0);
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [facets, setFacets] = useState<{ entity_types: string[]; actions: string[] }>({ entity_types: [], actions: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState<'csv' | 'json' | null>(null);

  const filters: AuditLogFilters = useMemo(() => {
    const f: AuditLogFilters = { realm };
    if (entityFilter !== 'all') f.entity_type = entityFilter;
    if (actionFilter !== 'all') f.action = actionFilter;
    if (from) f.from = from;
    if (to) f.to = to;
    if (search) f.q = search;
    return f;
  }, [realm, entityFilter, actionFilter, from, to, search]);

  // Debounce typing: one request per pause, not per keystroke.
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    if (!allowed) return;
    api.getRealms().then((r) => setRealms(r.data)).catch(() => setRealms([]));
  }, [allowed]);

  // cursors[i] loads page i (page 0 = null). The stack is keyed by the
  // filters + page size, so a filter change resets to page 1 IN THE SAME
  // RENDER; resetting in an effect would first fire one request with the new
  // filters and the old cursor.
  const pagingKey = JSON.stringify([filters, pageSize]);
  const [paging, setPaging] = useState<{ key: string; cursors: (string | null)[] }>({ key: pagingKey, cursors: [null] });
  const cursors = paging.key === pagingKey ? paging.cursors : [null];
  const pageIndex = cursors.length - 1;
  const cursor = cursors[pageIndex];
  const goNext = () => { if (nextCursor) setPaging({ key: pagingKey, cursors: [...cursors, nextCursor] }); };
  const goPrev = () => setPaging({ key: pagingKey, cursors: cursors.slice(0, -1) });
  const refresh = () => { setPaging({ key: pagingKey, cursors: [null] }); setReloadTick((t) => t + 1); };

  useEffect(() => {
    if (!allowed) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        // Until this response lands, the cursor we hold belongs to the previous
        // filters or page; offering Next with it would silently skip rows.
        setNextCursor(null);
        const res = await api.getAuditLogs(filters, { cursor, limit: pageSize });
        if (cancelled) return;
        setLogs(res.data);
        setNextCursor(res.next_cursor);
        if (res.total !== undefined) setTotal(res.total);
        if (res.facets) setFacets(res.facets);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load audit logs');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [allowed, filters, cursor, pageSize, reloadTick]);

  const exportAs = async (format: 'csv' | 'json') => {
    try {
      setExporting(format);
      setError(null);
      const { blob, filename } = await api.downloadAuditExport(filters, format);
      saveBlob(blob, filename);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Export failed');
    } finally {
      setExporting(null);
    }
  };

  if (!allowed) {
    return (
      <div className="p-4 lg:p-8">
        <Alert tone="warning">You don't have access to the audit log.</Alert>
      </div>
    );
  }

  const formatDateTime = (dateStr: string) => {
    return new Date(dateStr).toLocaleString('en-ZA', {
      timeZone: 'Africa/Johannesburg',
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  // Badge only has 7 tones for these 9 action colours, so some collapse is
  // unavoidable (reject/soft_delete already shared one raw colour before
  // this migration — that collision predates Badge). Exact/near hue
  // matches: green->success, red->danger, purple->purple, blue->info,
  // amber->warning, teal->success (gazette; success IS Tailwind's teal).
  // cyan has no Badge tone — routed to 'info' (its nearest blue-family
  // neighbour), same reasoning as `upload`'s indigo below.
  //
  // Cross-group indigo note (see also BillVerification.tsx in wave 2b
  // group D, which normalized an indigo *status-ladder* chip to 'success'):
  // `upload` has no such ladder, just a flat per-action colour, so there's
  // no semantic anchor to borrow. Badge has no indigo tone; decision here
  // is to route it to 'purple' as indigo's nearest available hue rather
  // than leave it hand-rolled. This does mean `upload` and `finalize` now
  // render as the same purple chip, disambiguated only by label text —
  // flagging for reconciliation in case another group's indigo decision
  // should take precedence app-wide.
  //
  // ── Fix-wave-2 item 5: RULING — the six pre-migration hues CANNOT be
  // restored from Badge's current tone set, so this map is left as-is. ──
  //
  // The review asked whether upload+finalize, gazette+approve and
  // assign_tariff+create could be pulled back apart. They can't, and the
  // reason is arithmetic before it is aesthetic:
  //
  //   9 distinct actions, 7 Badge tones -> >=2 collisions by pigeonhole,
  //   whatever the assignment.
  //
  //   `neutral` is further unavailable: it is this map's `?? 'neutral'`
  //   fallback for an action the SPA build doesn't know about (the API's
  //   action set can gain values ahead of a Pages deploy). Spending it on a
  //   *known* action makes an unrecognised action render identically to a
  //   recognised one — trading a labelled collision for an unlabelled one,
  //   which is strictly worse in an audit trail. That leaves 6 usable tones
  //   for 9 actions: >=3 collisions.
  //
  // Three collisions is exactly what this map has. It is already at the
  // floor; there is no assignment of the existing tones that produces six
  // distinguishable action hues.
  //
  // The one unspent tone is `orange`, which could break ONE of the three
  // pairs and take the count from 5 tones/3 collisions to 6 tones/2. Not
  // taken, deliberately: none of the six actions has an orange or
  // orange-adjacent pre-migration hue to anchor to (they were indigo,
  // purple, teal, green, cyan and blue), so picking a winner would be an
  // arbitrary recolour; and `orange` already carries a specific meaning
  // everywhere else in this SPA — the "error whose message starts with
  // `Rejected:`" chip in BillRuns.tsx and BillVerification.tsx. Making
  // `gazette` or `upload` render in the rejection colour would be actively
  // misleading on an audit line. Per the standing constraint, no new tone is
  // invented to force six: adding one means a hex nobody has contrast-tested
  // in both themes, and Badge's tones are policed pair-by-pair in
  // src/test/contrast.test.ts precisely so that can't happen by hand.
  //
  // The information the collisions cost is recoverable: every chip carries
  // the action name as its label, and the Action filter <select> above
  // narrows to one action exactly. If six-way hue discrimination is later
  // judged worth it, the fix is to widen Badge's tone set deliberately —
  // with contrast entries in src/lib/theme.ts and assertions in
  // contrast.test.ts — not to reshuffle this map.
  const ACTION_TONE: Record<string, BadgeTone> = {
    approve: 'success',
    reject: 'danger',
    finalize: 'purple',
    create: 'info',
    update: 'warning',
    upload: 'purple',
    soft_delete: 'danger',
    gazette: 'success',
    assign_tariff: 'info',
  };

  // GENERAL RULE (not just a Badge rule): no primary column may hold content
  // that can neither wrap (no natural break point) nor be bounded (no
  // max-width + truncate). Badge is one instance — it forces
  // whitespace-nowrap — but this table reproduced the identical hazard with
  // two raw-CSS primaries instead: `time` via a literal whitespace-nowrap,
  // `user` via a completely unguarded Keycloak email (`@`/`.` aren't break
  // opportunities). Both are now bounded below, the same way `details`
  // (secondary) already was.
  //
  // Three primaries were considered — Time/Entity/User, "no selection, no
  // rowActions" per the BillRunDashboard precedent (Phase V2) — but that
  // precedent doesn't transfer cleanly here. BillRunDashboard's meter tables
  // run `density="compact"` and two of their three primaries are numeric
  // (short, bounded by nature). This table runs the `density="comfortable"`
  // default (px-5 padding on every cell) inside a `p-8` page container, and
  // two of the three candidate primaries (Entity, User) are unbounded free
  // text. At 375px that's ~48px toggle + 3×40px of cell padding before any
  // content is drawn — leaving well under 150px combined for Time + Entity +
  // User's actual text; Time alone needs ~110-130px for its ~18-char
  // en-ZA output, so Entity and User would be squeezed to a couple of
  // characters each. Two primaries fit; three don't.
  //
  // `entity` is demoted to secondary: Action (already secondary) already
  // conveys much of "what happened", and the full entity type + id is one
  // tap away in the row detail. Time + User stay primary — when it
  // happened and who did it are the two fields worth an at-a-glance scan
  // on an audit line; what changed is the one a reader is most willing to
  // expand for.
  const logColumns: DataTableColumn<AuditLog>[] = [
    {
      key: 'time',
      label: 'Time (SAST)',
      priority: 'primary',
      // Bounded + truncated instead of forced nowrap: the datetime string
      // can't be usefully broken mid-token either way, but an unbounded
      // whitespace-nowrap span can still force the row wider than the
      // viewport when it shares space with another primary. title recovers
      // the full value in the (expected-rare) case this actually clips.
      render: (log) => {
        const value = formatDateTime(log.created_at);
        return (
          <span className="text-xs text-content-muted max-w-[10rem] truncate block" title={value}>
            {value}
          </span>
        );
      },
    },
    {
      key: 'realm',
      label: 'Realm',
      priority: 'secondary',
      render: (log) => <span className="text-xs font-mono text-content-muted">{log.realm || '-'}</span>,
    },
    {
      key: 'action',
      label: 'Action',
      priority: 'secondary',
      render: (log) => (
        <Badge tone={ACTION_TONE[log.action] ?? 'neutral'}>
          {log.action.replace(/_/g, ' ')}
        </Badge>
      ),
    },
    {
      key: 'entity',
      label: 'Entity',
      priority: 'secondary',
      render: (log) => (
        <span className="text-xs text-content-muted">
          <span className="font-medium capitalize">{log.entity_type.replace(/_/g, ' ')}</span>
          <span className="text-content-muted ml-1 font-mono">{log.entity_id.substring(0, 8)}</span>
        </span>
      ),
    },
    {
      key: 'user',
      label: 'User',
      priority: 'primary',
      // A Keycloak email has no reliable break opportunity (`@`/`.` aren't
      // break points) and was previously rendered completely unguarded —
      // same treatment `details` already has (max-w + truncate + block),
      // plus a title since this is a primary column: the value must stay
      // recoverable on hover/long-press when it clips.
      render: (log) => {
        const value = log.user_email || log.user_id.substring(0, 12);
        return (
          <span className="text-xs text-content-muted max-w-xs truncate block" title={value}>
            {value}
          </span>
        );
      },
    },
    {
      key: 'details',
      label: 'Details',
      priority: 'secondary',
      // title: the full text on hover/long-press; the row's expand toggle
      // shows it formatted (AuditDetails).
      render: (log) => (
        <span className="text-xs text-content-muted max-w-xs truncate block" title={log.details ?? undefined}>
          {log.details || '-'}
        </span>
      ),
    },
  ];

  return (
    <div className="p-4 lg:p-8">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-8">
        <h1 className="text-2xl font-bold text-content">Audit Logs</h1>
        <div className="flex flex-wrap items-center gap-2">
          <button type="button" onClick={() => exportAs('csv')} disabled={exporting !== null}
            className="tap-target lg:min-h-0 px-4 py-2 text-content bg-surface-sunken rounded-lg hover:bg-border-token text-sm disabled:opacity-50">
            {exporting === 'csv' ? 'Exporting…' : 'Export CSV'}
          </button>
          <button type="button" onClick={() => exportAs('json')} disabled={exporting !== null}
            className="tap-target lg:min-h-0 px-4 py-2 text-content bg-surface-sunken rounded-lg hover:bg-border-token text-sm disabled:opacity-50">
            {exporting === 'json' ? 'Exporting…' : 'Export JSON'}
          </button>
          <button type="button" onClick={refresh}
            className="tap-target lg:min-h-0 px-4 py-2 text-content bg-surface-sunken rounded-lg hover:bg-border-token text-sm">
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <Alert tone="danger" className="mb-6">
          {error}
        </Alert>
      )}

      {/* Filters: applied server-side, so they cover every entry, not just the loaded page. */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <input type="text" placeholder="Search logs..." value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)} className={`${SELECT} w-full sm:w-64`} />
        <label className="sr-only" htmlFor="audit-realm">Realm</label>
        <select id="audit-realm" value={realm} onChange={(e) => setRealm(e.target.value)} className={SELECT}>
          <option value="*">All realms</option>
          {[...new Set([realm === '*' ? '' : realm, ...realms.map((r) => r.realm)])].filter(Boolean).map((r) => (
            <option key={r} value={r}>{r}</option>
          ))}
        </select>
        <select value={entityFilter} onChange={(e) => setEntityFilter(e.target.value)} className={SELECT} aria-label="Entity">
          <option value="all">All Entities</option>
          {/* The current choice stays listed even when the new realm's facets
              lack it: the filter still applies, so the select must show it. */}
          {withSelected(facets.entity_types, entityFilter).map((e) => <option key={e} value={e}>{e.replace(/_/g, ' ')}</option>)}
        </select>
        <select value={actionFilter} onChange={(e) => setActionFilter(e.target.value)} className={SELECT} aria-label="Action">
          <option value="all">All Actions</option>
          {withSelected(facets.actions, actionFilter).map((a) => <option key={a} value={a}>{a.replace(/_/g, ' ')}</option>)}
        </select>
        <label className="text-sm text-content-muted flex items-center gap-2">From
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className={SELECT} />
        </label>
        <label className="text-sm text-content-muted flex items-center gap-2">To
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className={SELECT} />
        </label>
        <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))} className={SELECT} aria-label="Page size">
          {[10, 25, 50, 100].map((n) => <option key={n} value={n}>{n} per page</option>)}
        </select>
        <span className="text-sm text-content-muted sm:ml-auto">{total} log{total !== 1 ? 's' : ''}</span>
      </div>

      {/* Table */}
      {loading ? (
        <Card className="shadow-sm p-8">
          <div className="animate-pulse space-y-4">
            {[1, 2, 3, 4, 5].map(i => <div key={i} className="h-10 bg-border-token rounded" />)}
          </div>
        </Card>
      ) : logs.length === 0 ? (
        <Card className="shadow-sm px-6 py-12 text-center text-content-muted">
          <p className="text-lg font-medium">No audit logs found</p>
          <p className="mt-1">{search || entityFilter !== 'all' || actionFilter !== 'all' || from || to ? 'Try adjusting your filters' : 'Activity will appear here as actions are performed'}</p>
        </Card>
      ) : (
        <Card className="shadow-sm">
          <DataTable
            data={logs}
            columns={logColumns}
            rowKey={(log) => log.id}
            renderExpanded={(log) => <AuditDetails details={log.details} />}
          />
        </Card>
      )}

      {/* Pagination: keyset cursors from the server, so Previous/Next only (no page count or jump). */}
      {(pageIndex > 0 || nextCursor) && (
        <div className="flex items-center justify-between mt-4 gap-3">
          <p className="text-sm text-content-muted">Page {pageIndex + 1}</p>
          <div className="flex items-center gap-2">
            <button type="button" onClick={goPrev} disabled={pageIndex === 0}
              className="tap-target lg:min-h-0 px-3 py-1.5 text-sm border border-border-token rounded-md hover:bg-surface-sunken disabled:opacity-40 disabled:cursor-not-allowed">Previous</button>
            <button type="button" onClick={goNext} disabled={!nextCursor}
              className="tap-target lg:min-h-0 px-3 py-1.5 text-sm border border-border-token rounded-md hover:bg-surface-sunken disabled:opacity-40 disabled:cursor-not-allowed">Next</button>
          </div>
        </div>
      )}
    </div>
  );
}
